import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, FormCheck, Row, Table } from 'react-bootstrap';
import { CircleFill } from 'react-bootstrap-icons';
import { getScenarioOptions, monthSmall } from '../../../../../../helpers/constants';
import { getTranslatedMonth } from '../../../../../../helpers/localization';
import useLocalization from '../../../../../../hooks/useLocalization';
import useAuth from '../../../../../../hooks/useAuth';
import DropdownSelector from '../../../../../common/DropdownSelector';
import LTRNumberCell from '../../../../../common/LTRNumberCell';
import StrategyDifferences from '../../../../../common/StrategyDifferences';
import SwitchInput from '../../../../../common/SwitchInput';
import LineChart from '../../../../../common/line-chart/LineChart';
import ArmageddonMultipleStrategyModal from '../../strategies/ArmageddonMultipleStrategyModal';
import {
  addMonthAndYear,
  calculateBalanceSheet,
  cloneStrategy,
  findWeakestMonthYearOfStrategy,
  formatCurrency,
  getChartColor,
  getFinalYearOfStrategy,
  getMonthlyCompoundedDepreciation,
  getNextNYear,
  getValueAtMonthYear
} from '../helper';
import ChartContainer from './ChartContainer';
import { isCustomer } from '../../../../../../helpers/global';
import Legend from './Legend';

export const getMetricOptions = translate => [
  { name: translate('equity'), key: 'equity' },
  { name: translate('assets'), key: 'assets' },
  { name: translate('liabilities'), key: 'liabilities' }
];

const createChartData = (
  strategies = [],
  activeMetric,
  activeScenario,
  armageddonDetails,
  translate,
  stressTestApplied,
  adminCompTable,
  customerCompTable,
  strategyYears,
  endAllAtTheEnd
) => {
  const labels = [];

  const getMonthlyValue = (strategy, sectionName, monthNum, year) => {
    return strategy[sectionName]
      .filter(a => !a.isInvalid && !a.toBeMerged)
      .reduce((prevValue, a) => prevValue + getValueAtMonthYear(a, monthNum, year), 0);
  };

  const strategyDatasets = [];

  strategies.forEach((originalStrategy, index) => {
    const clonedStrategy = cloneStrategy(originalStrategy);
    if (activeScenario.changeInPercentage) {
      clonedStrategy.assets.forEach(a => {
        a.returnAppreciation = a.returnAppreciation + activeScenario.changeInPercentage * a.returnAppreciation;
      });
    }

    if (endAllAtTheEnd) {
      const { month: finalMonth, year: finalYear } = addMonthAndYear(
        clonedStrategy.initialMonth,
        clonedStrategy.initialYear,
        0,
        clonedStrategy.totalYears ?? 10
      );

      //sell all if not sold at the end
      clonedStrategy.assets
        .filter(a => !['Cash'].includes(a.category))
        .forEach(a => {
          if (!a.sellingYear) {
            a.sellingYear = finalYear;
            a.sellingMonth = finalMonth;
          }
        });

      [...clonedStrategy.liabilities, ...clonedStrategy.fixedIncomeAssets].forEach(l => {
        if (!l.endYear) {
          l.endYear = finalYear;
          l.endMonth = finalMonth;
        }
      });
    }

    const armageddon = activeScenario.isArmageddon &&
      armageddonDetails && {
        ...armageddonDetails,
        assetDepreciations: armageddonDetails.strategyAssetDepreciations.find(
          s => s.strategyName === originalStrategy.name
        )?.assetDepreciations
      };

    const strategy = calculateBalanceSheet(
      clonedStrategy,
      armageddon,
      stressTestApplied,
      translate,
      false,
      adminCompTable,
      customerCompTable
    );

    const strategyColor = getChartColor(index);
    const thisStrategyDatapoints = [];

    const initialMonth = strategy.initialMonth;
    const initialYear = strategy.initialYear;

    const { month: terminalMonth, year: terminalYear } = addMonthAndYear(
      initialMonth,
      initialYear,
      0,
      strategy.totalYears ?? 10
    );

    [...strategyYears, strategyYears[strategyYears.length - 1] + 1].forEach((year, yearIndex) => {
      monthSmall.forEach((month, monthNum) => {
        if (yearIndex === 0 && monthNum < initialMonth) return;
        if (year > terminalYear || (year === terminalYear && monthNum > terminalMonth)) return;

        const label = `${getTranslatedMonth(translate, monthNum, { short: true })} ${year}`;
        if (!labels.includes(label)) labels.push(label);

        let value;
        if (activeMetric.key === 'equity') {
          const assetValueAtThisMonth = getMonthlyValue(strategy, 'assets', monthNum, year);
          const liabilityValueAtThisMonth = getMonthlyValue(strategy, 'liabilities', monthNum, year);
          value = assetValueAtThisMonth - liabilityValueAtThisMonth;
        } else {
          value = getMonthlyValue(strategy, activeMetric.key, monthNum, year);
        }

        thisStrategyDatapoints.push(value.toFixed(0));
      });
    });

    strategyDatasets.push({
      label: strategy.name,
      _id: strategy._id,
      isSandboxed: strategy.isSandboxed,
      data: thisStrategyDatapoints,
      borderColor: strategyColor,
      backgroundColor: strategyColor
    });
  });

  return {
    labels,
    datasets: strategyDatasets
  };
};

const ComparisonTable = ({ translate, chartData, hoverIndex, visibleStrategies, isRTL }) => {
  const comparisonData = useMemo(() => {
    const dataIndex = hoverIndex ?? chartData.datasets[0].data.length - 1;

    return {
      label: chartData.labels[dataIndex],
      data: chartData.datasets
        .filter((_, index) => visibleStrategies[index])
        .sort((a, b) => Number(a.data[dataIndex]) - Number(b.data[dataIndex]))
        .map((d, index, datasets) => ({
          _id: d._id,
          name: d.label,
          color: d.backgroundColor,
          value: d.data[dataIndex],
          diffFromPrevious: index === 0 ? d.data[dataIndex] : d.data[dataIndex] - datasets[index - 1].data[dataIndex]
        }))
    };
  }, [chartData, hoverIndex, visibleStrategies]);

  return (
    <>
      <h6 className="mid fw-bold text-end">
        <i>Date: {comparisonData.label}</i>
      </h6>
      <div className="mid position-relative  my-2">
        <Table hover bordered className="mid   my-2">
          <thead className="sticky-header">
            <tr>
              <th className="text-white bg-table-header">{translate('strategy')}</th>
              <th className="text-white bg-table-header">{translate('value')}</th>
              <th className="text-white bg-table-header">{translate('difference')}</th>
            </tr>
          </thead>
          <tbody>
            {comparisonData.data.map((r, index) => (
              <tr key={r._id}>
                <td className="d-flex align-items-center">
                  <div style={{ width: 'fit-content' }}>
                    <CircleFill size={10} style={{ color: r.color }} />
                  </div>
                  <b className="mx-1"> {r.name}</b>
                </td>
                <td>{formatCurrency(r.value)} </td>
                <td>
                  {index === 0 ? (
                    '_'
                  ) : (
                    <>
                      {!isRTL && <span className="fw-bold">+</span>}
                      <LTRNumberCell value={r.diffFromPrevious} />
                      {isRTL && <span className="fw-bold">+</span>}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>{' '}
    </>
  );
};

const ComparisonChart = ({
  customer,
  strategies: originalStrategies = [],
  activeStrategy,
  adminCompTable,
  customerCompTable
}) => {
  const { translate, langCode, isRTL } = useLocalization();
  const { user } = useAuth();
  const showStressTestAndArmaggedonToClient = useMemo(() => adminCompTable.appConfig.showStressTestToClient, [
    adminCompTable
  ]);
  const scenarioOptions = useMemo(() => getScenarioOptions(translate, showStressTestAndArmaggedonToClient), [
    langCode,
    showStressTestAndArmaggedonToClient
  ]);
  const metricOptions = useMemo(() => getMetricOptions(translate), [langCode, user]);

  const [chartData, setChartData] = useState();
  const [activeMetric, setActiveMetric] = useState(metricOptions[0]);
  const [activeScenario, setActiveScenario] = useState(scenarioOptions[0]);
  const [armageddonDetails, setArmageddonDetails] = useState(null);
  const [armageddonDetailsModalMeta, setArmageddonDetailsModalMeta] = useState(null);
  const [stressTestApplied, setStressTestApplied] = useState(false);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [isDifferenceMode, setIsDifferenceMode] = useState(false);
  const isFreemiumCustomer = useMemo(() => customer.level <= 1, [customer]);
  console.log('isFreemiumCustomer', isFreemiumCustomer);

  const strategies = useMemo(() => {
    // Filter out killed strategies
    const activeStrategies = originalStrategies.filter(s => !s.killed && (!isFreemiumCustomer || s.isSandboxed));

    // Identify parent strategies (those without a sandbox parent)
    const parentStrategies = activeStrategies.filter(s => !s.sandbox?.parentStrategy);

    // Attach sandbox strategies to each parent
    const sortedStrategies = parentStrategies.flatMap(parent => {
      const sandboxStrategies = activeStrategies.filter(s => s.sandbox?.parentStrategy === parent._id);

      return [parent, ...sandboxStrategies];
    });

    return sortedStrategies;
  }, [originalStrategies]);

  const chartStartYear = useMemo(() => Math.min(...strategies.map(s => s.initialYear)), [strategies]);
  const chartEndYear = useMemo(() => Math.max(...strategies.map(s => getFinalYearOfStrategy(s))), [strategies]);

  const strategyYears = useMemo(() => getNextNYear(chartStartYear, chartEndYear - chartStartYear + 1), [
    chartStartYear,
    chartEndYear
  ]);

  const [visibleStrategies, setVisibleStrategies] = useState(strategies.map(() => true));
  const [endAllAtTheEnd, setEndAllAtTheEnd] = useState(false);

  useEffect(() => {
    let strategy = strategies[0];
    !strategy?.isSandboxed ? setVisibleStrategies([strategies[0]]) : setVisibleStrategies([]);
  }, [strategies]);

  useEffect(() => {
    setChartData(
      createChartData(
        strategies,
        activeMetric,
        activeScenario,
        armageddonDetails,
        translate,
        stressTestApplied,
        adminCompTable,
        customerCompTable,
        strategyYears,
        endAllAtTheEnd
      )
    );
  }, [strategies, activeMetric, activeScenario, armageddonDetails, stressTestApplied, endAllAtTheEnd]);

  const onArmageddonStrategyAssetDepreciationChange = (strategyName, assetId, depreciation) => {
    const { strategyAssetDepreciations = [] } = armageddonDetailsModalMeta;

    const updatedAsset = strategyAssetDepreciations
      .find(s => s.strategyName === strategyName)
      .assetDepreciations.find(a => a._id === assetId);

    if (updatedAsset) {
      updatedAsset.depreciation = depreciation;
      setArmageddonDetailsModalMeta({ ...armageddonDetailsModalMeta, strategyAssetDepreciations });
    }
  };

  const onApplyArmageddon = () => {
    setArmageddonDetails({
      ...armageddonDetailsModalMeta,
      strategyAssetDepreciations: armageddonDetailsModalMeta.strategyAssetDepreciations.map(s => ({
        ...s,
        assetDepreciations: s.assetDepreciations.map(a => ({
          ...a,
          monthlyDepreciation: getMonthlyCompoundedDepreciation(18, a.depreciation)
        }))
      }))
    });
    setArmageddonDetailsModalMeta(null);
  };

  const onActiveScenarioChange = scenario => {
    setActiveScenario(scenario);
    if (scenario.isArmageddon) {
      setArmageddonDetails({
        startMonth: 0,
        startYear: Math.min(...strategies.map(s => s.initialYear)) + 1,
        strategyAssetDepreciations: strategies.map(s => ({
          strategyName: s.name,
          assetDepreciations: s.assets
            .filter(a => ['Stocks', 'Real Estate'].includes(a.category))
            .map(a => ({
              _id: a._id,
              name: a.name,
              depreciation: a.crashPercentage,
              monthlyDepreciation: getMonthlyCompoundedDepreciation(18, a.crashPercentage)
            }))
        }))
      });
    }
  };

  const onArmagedonClick = () => {
    setArmageddonDetailsModalMeta({ ...armageddonDetails });
  };

  const onArmageddonWeakestTimeClick = () => {
    const strategy = strategies.find(s => s._id === activeStrategy._id);
    const weakestMonthYear = findWeakestMonthYearOfStrategy(
      calculateBalanceSheet(strategy, null, null, translate, false, adminCompTable, customerCompTable)
    );

    setArmageddonDetailsModalMeta({
      ...armageddonDetailsModalMeta,
      startYear: weakestMonthYear.year,
      startMonth: weakestMonthYear.month
    });
  };

  return (
    <>
      <ChartContainer
        title={translate('strategies_comparison')}
        maxWidth={'100%'}
        minHeight={500}
        renderAdditionalComponent={() => (
          <div className="d-flex align-items-center">
            {showStressTestAndArmaggedonToClient && (
              <FormCheck
                onChange={e => setStressTestApplied(e.target.checked)}
                checked={stressTestApplied}
                className="large mx-1"
                inline
                label={translate('apply_stress_test')}
              />
            )}

            <DropdownSelector
              className="mx-1"
              options={metricOptions.map(m => m.name)}
              value={activeMetric.name}
              onValueChange={metricName => setActiveMetric(metricOptions.find(m => m.name === metricName))}
            />

            <DropdownSelector
              className="mx-1"
              options={scenarioOptions.map(s => s.name)}
              value={activeScenario.name}
              onValueChange={scenarioName => onActiveScenarioChange(scenarioOptions.find(o => o.name === scenarioName))}
            />
            {showStressTestAndArmaggedonToClient && activeScenario.isArmageddon && armageddonDetails && (
              <div className="d-inline-block mx-1">
                <Button
                  style={{
                    padding: '0px 5px',
                    fontSize: 13
                  }}
                  size="sm"
                  variant="danger"
                  onClick={onArmagedonClick}
                >
                  {translate('configure_armageddon')}
                </Button>
              </div>
            )}

            <div className="bg-dark" style={{ height: 18, width: 1 }}></div>
            <SwitchInput
              onChange={value => setEndAllAtTheEnd(value)}
              value={endAllAtTheEnd}
              label={translate('after_tax')}
            />
          </div>
        )}
      >
        {(!user || !isCustomer(user.role)) && (
          <div className="mb-2">
            <SwitchInput
              onChange={value => setIsDifferenceMode(value)}
              value={isDifferenceMode}
              label={translate('show_differences')}
            />
          </div>
        )}

        <div style={{ height: 400 }} className={`overflow-auto ${isDifferenceMode ? 'scrollbar-visible' : ''}`}>
          {isDifferenceMode && (!user || !isCustomer(user.role)) ? (
            <StrategyDifferences
              customer={customer}
              comparisonStrategies={strategies.filter((s, index) => visibleStrategies[index])}
            />
          ) : (
            <>
              {chartData && (
                <Row className="h-100">
                  <Col xs={12} md={8} lg={8} className="px-2 h-100">
                    <LineChart
                      onMouseHover={hoverPoint => setHoverIndex(hoverPoint.index)}
                      onMouseExit={() => setHoverIndex(null)}
                      visibleLines={visibleStrategies}
                      onVisibleLinesChange={setVisibleStrategies}
                      chartData={chartData}
                      hideLegend
                      chartHeight={350}
                    />
                  </Col>
                  <Col xs={12} md={4} lg={4} className="h-100 overflow-auto scrollbar-visible">
                    <ComparisonTable
                      translate={translate}
                      isRTL={isRTL}
                      hoverIndex={hoverIndex}
                      chartData={chartData}
                      visibleStrategies={visibleStrategies}
                    />
                  </Col>
                </Row>
              )}
            </>
          )}
        </div>

        <div className="mt-3">
          {!isFreemiumCustomer && (
            <>
              <h6 className="smallFont">{translate('strategist_strategies')}</h6>
              <Legend
                className="mb-2"
                legends={chartData?.datasets
                  .map((d, index) => ({ ...d, selected: visibleStrategies[index] }))
                  .filter(d => !d.isSandboxed)}
                onSelectAllClick={() => {
                  const isSelected = strategies.some((s, index) => !s.isSandboxed && visibleStrategies[index]);

                  setVisibleStrategies(
                    strategies.map((s, index) => (s.isSandboxed ? visibleStrategies[index] : !isSelected))
                  );
                }}
                onLegendClick={legend => {
                  setVisibleStrategies(
                    strategies.map((s, index) =>
                      s._id === legend._id ? !visibleStrategies[index] : visibleStrategies[index]
                    )
                  );
                }}
              />
            </>
          )}
          <h6 className="smallFont mt-2">{translate('diy_strategies')}</h6>
          <Legend
            legends={chartData?.datasets
              .map((d, index) => ({ ...d, selected: visibleStrategies[index] }))
              .filter(d => d.isSandboxed)}
            onSelectAllClick={() => {
              const isSelected = strategies.some((s, index) => s.isSandboxed && visibleStrategies[index]);

              setVisibleStrategies(
                strategies.map((s, index) => (!s.isSandboxed ? visibleStrategies[index] : !isSelected))
              );
            }}
            onLegendClick={legend => {
              setVisibleStrategies(
                strategies.map((s, index) =>
                  s._id === legend._id ? !visibleStrategies[index] : visibleStrategies[index]
                )
              );
            }}
          />
        </div>
      </ChartContainer>
      {activeScenario.isArmageddon && (
        <ArmageddonMultipleStrategyModal
          strategyYears={strategyYears}
          show={armageddonDetailsModalMeta !== null}
          onHide={() => setArmageddonDetailsModalMeta(null)}
          strategyAssetDepreciations={armageddonDetailsModalMeta?.strategyAssetDepreciations}
          onStrategyAssetDepreciationsChange={onArmageddonStrategyAssetDepreciationChange}
          startYear={armageddonDetailsModalMeta?.startYear}
          onStartYearChange={year => setArmageddonDetailsModalMeta({ ...armageddonDetailsModalMeta, startYear: year })}
          startMonth={armageddonDetailsModalMeta?.startMonth}
          onStartMonthChange={month =>
            setArmageddonDetailsModalMeta({ ...armageddonDetailsModalMeta, startMonth: month })
          }
          onWeakestTimeClick={onArmageddonWeakestTimeClick}
          onApplyArmageddon={onApplyArmageddon}
          translate={translate}
        />
      )}
    </>
  );
};

export default ComparisonChart;
